<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import type { NuxtError } from '#app'
import TeachmeCheckSymbol from '~/components/atoms/logos/TeachmeCheckSymbol.vue'
import BaseCluster from '~/components/foundations/BaseCluster.vue'
import BaseStack from '~/components/foundations/BaseStack.vue'
import TextBody from '~/components/foundations/TextBody.vue'
import { useAuth } from '~/composables/useAuth'
import { isIpNotAllowedError as isIpNotAllowedErrorRaw } from '~/utils/error'

useHead({
  titleTemplate: 'Teachme Check',
})

const props = defineProps<{
  error: NuxtError
}>()

const { t } = useI18n()
const { signOut } = useAuth()

// エラー判定が多くなってきたらcomposableに切り出すと良さそう
const isChunkError = computed(() => {
  return props.error.message.includes('Failed to fetch dynamically imported module')
})

const isIpNotAllowedError = computed(() => {
  return isIpNotAllowedErrorRaw(props.error)
})

const errorMessage = computed<string | null>(() => {
  if (isChunkError.value) {
    // [NOTE]
    // デプロイのたびにchunkのハッシュが変わるため、ロード済みのmanifestではdynamic importに失敗しエラーページが表示されてしまう。
    // エラーが発生しても組み込みのpluginによってハードリロードされ新しいchunkが読み込まれて問題ないケースであるためエラーメッセージは表示しないようにする。
    // しかしデプロイ起因以外の同エラーが発生する可能性も考えて、戻るリンクは表示する
    // https://nuxt.com/docs/getting-started/error-handling#errors-with-js-chunks
    return null
  } else if (props.error.statusCode === 404) {
    return t('ページが見つかりませんでした。')
  } else if (isIpNotAllowedError.value) {
    return t(
      'こちらのネットワークからの利用は許可されていません。許可されたネットワークからアクセスしてください。'
    )
  }

  return t('エラーが発生しました。')
})

const hasReturnLink = computed(() => {
  return !isIpNotAllowedError.value
})

const logout = async () => {
  await signOut()
  await navigateTo({ name: 'login' })
}
</script>

<template>
  <BaseCluster :align="'center'" justify="center" class="error-page">
    <BaseStack :align="'center'" gap="l">
      <TeachmeCheckSymbol height="87px" width="90px" />

      <TextBody v-if="errorMessage" size="large">{{ errorMessage }}</TextBody>
      <NuxtLink v-if="hasReturnLink" :to="{ name: 'index' }" class="m-2">{{
        $t('TOPページに戻る')
      }}</NuxtLink>
      <button v-if="isIpNotAllowedError" class="logout-button" @click="logout">
        <TextBody size="large" color="success-default" class="logout-button-label">{{
          $t('ログアウト')
        }}</TextBody>
      </button>
    </BaseStack>
  </BaseCluster>
</template>

<style scoped lang="scss">
.error-page {
  height: 100vh;
  padding: spacingToken.$spacing-m;
  white-space: pre-wrap;
  text-align: center;
}

.logout-button {
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
}

.logout-button-label {
  text-decoration: underline;
}
</style>
